const DEFAULT_OPTIONS = Object.freeze({
  tolerance: 30,
  featherRange: 18,
  minEdgeCoverage: 0.72,
  maxRemovedRatio: 0.97,
  minRetainedOpaqueRatio: 0.015,
  transparentAlpha: 12,
});

function squaredColorDistance(data, offset, color) {
  const red = data[offset] - color.red;
  const green = data[offset + 1] - color.green;
  const blue = data[offset + 2] - color.blue;
  return (red * red) + (green * green) + (blue * blue);
}

function collectEdgePixelIndices(width, height) {
  const indices = [];
  const seen = new Set();
  const add = (x, y) => {
    const pixelIndex = (y * width) + x;
    if (seen.has(pixelIndex)) return;
    seen.add(pixelIndex);
    indices.push(pixelIndex);
  };

  for (let x = 0; x < width; x += 1) {
    add(x, 0);
    if (height > 1) add(x, height - 1);
  }
  for (let y = 1; y < height - 1; y += 1) {
    add(0, y);
    if (width > 1) add(width - 1, y);
  }

  return indices;
}

function estimateEdgeBackground(data, edgePixelIndices, transparentAlpha) {
  const bins = new Map();
  let opaqueEdgePixels = 0;
  let transparentEdgePixels = 0;

  for (const pixelIndex of edgePixelIndices) {
    const offset = pixelIndex * 4;
    if (data[offset + 3] <= transparentAlpha) {
      transparentEdgePixels += 1;
      continue;
    }

    opaqueEdgePixels += 1;
    const key = `${data[offset] >> 5}:${data[offset + 1] >> 5}:${data[offset + 2] >> 5}`;
    const bin = bins.get(key) || { count: 0, red: 0, green: 0, blue: 0 };
    bin.count += 1;
    bin.red += data[offset];
    bin.green += data[offset + 1];
    bin.blue += data[offset + 2];
    bins.set(key, bin);
  }

  if (opaqueEdgePixels === 0 || bins.size === 0) {
    return {
      color: null,
      opaqueEdgePixels,
      transparentEdgePixels,
    };
  }

  let dominant = null;
  for (const bin of bins.values()) {
    if (!dominant || bin.count > dominant.count) dominant = bin;
  }

  return {
    color: {
      red: Math.round(dominant.red / dominant.count),
      green: Math.round(dominant.green / dominant.count),
      blue: Math.round(dominant.blue / dominant.count),
    },
    opaqueEdgePixels,
    transparentEdgePixels,
  };
}

function skippedResult(source, width, height, reason, metadata = {}) {
  return {
    data: new Uint8ClampedArray(source),
    width,
    height,
    cleanup: {
      applied: false,
      reason,
      removedOpaquePixels: 0,
      featheredPixels: 0,
      ...metadata,
    },
  };
}

/**
 * Removes only a near-uniform background that is connected to an image edge.
 * The source pixel array is never mutated. This is intentionally a mockup-only
 * visual approximation; the original customer file remains authoritative.
 */
export function removeEdgeConnectedBackground(imageData, options = {}) {
  const width = Number(imageData?.width) || 0;
  const height = Number(imageData?.height) || 0;
  const source = imageData?.data;
  if (!source || width < 2 || height < 2 || source.length !== width * height * 4) {
    return skippedResult(source || [], width, height, 'invalid_image_data');
  }

  const settings = { ...DEFAULT_OPTIONS, ...options };
  const edgePixelIndices = collectEdgePixelIndices(width, height);
  const edge = estimateEdgeBackground(source, edgePixelIndices, settings.transparentAlpha);
  const transparentEdgeRatio = edgePixelIndices.length
    ? edge.transparentEdgePixels / edgePixelIndices.length
    : 0;

  if (!edge.color || transparentEdgeRatio >= 0.5) {
    return skippedResult(source, width, height, 'already_transparent', {
      transparentEdgeRatio,
    });
  }

  const toleranceSquared = settings.tolerance * settings.tolerance;
  let matchingOpaqueEdgePixels = 0;
  for (const pixelIndex of edgePixelIndices) {
    const offset = pixelIndex * 4;
    if (
      source[offset + 3] > settings.transparentAlpha &&
      squaredColorDistance(source, offset, edge.color) <= toleranceSquared
    ) {
      matchingOpaqueEdgePixels += 1;
    }
  }

  const edgeCoverage = edge.opaqueEdgePixels
    ? matchingOpaqueEdgePixels / edge.opaqueEdgePixels
    : 0;
  if (edgeCoverage < settings.minEdgeCoverage) {
    return skippedResult(source, width, height, 'non_uniform_edge', {
      backgroundColor: edge.color,
      edgeCoverage,
      transparentEdgeRatio,
    });
  }

  const totalPixels = width * height;
  const output = new Uint8ClampedArray(source);
  const visited = new Uint8Array(totalPixels);
  const queue = new Int32Array(totalPixels);
  let queueStart = 0;
  let queueEnd = 0;
  let opaquePixels = 0;

  for (let pixelIndex = 0; pixelIndex < totalPixels; pixelIndex += 1) {
    if (source[(pixelIndex * 4) + 3] > settings.transparentAlpha) opaquePixels += 1;
  }

  const canRemove = (pixelIndex) => {
    const offset = pixelIndex * 4;
    return source[offset + 3] <= settings.transparentAlpha ||
      squaredColorDistance(source, offset, edge.color) <= toleranceSquared;
  };

  for (const pixelIndex of edgePixelIndices) {
    if (visited[pixelIndex] || !canRemove(pixelIndex)) continue;
    visited[pixelIndex] = 1;
    queue[queueEnd] = pixelIndex;
    queueEnd += 1;
  }

  let removedOpaquePixels = 0;
  while (queueStart < queueEnd) {
    const pixelIndex = queue[queueStart];
    queueStart += 1;
    const offset = pixelIndex * 4;
    if (source[offset + 3] > settings.transparentAlpha) {
      output[offset + 3] = 0;
      removedOpaquePixels += 1;
    }

    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);
    const neighbors = [];
    if (x > 0) neighbors.push(pixelIndex - 1);
    if (x + 1 < width) neighbors.push(pixelIndex + 1);
    if (y > 0) neighbors.push(pixelIndex - width);
    if (y + 1 < height) neighbors.push(pixelIndex + width);

    for (const neighbor of neighbors) {
      if (visited[neighbor] || !canRemove(neighbor)) continue;
      visited[neighbor] = 1;
      queue[queueEnd] = neighbor;
      queueEnd += 1;
    }
  }

  const removedRatio = opaquePixels ? removedOpaquePixels / opaquePixels : 0;
  const retainedOpaqueRatio = opaquePixels
    ? (opaquePixels - removedOpaquePixels) / opaquePixels
    : 0;
  if (
    removedOpaquePixels === 0 ||
    removedRatio > settings.maxRemovedRatio ||
    retainedOpaqueRatio < settings.minRetainedOpaqueRatio
  ) {
    return skippedResult(source, width, height, 'insufficient_foreground', {
      backgroundColor: edge.color,
      edgeCoverage,
      removedRatio,
      retainedOpaqueRatio,
      transparentEdgeRatio,
    });
  }

  const featherLimit = settings.tolerance + settings.featherRange;
  const featherLimitSquared = featherLimit * featherLimit;
  let featheredPixels = 0;
  for (let pixelIndex = 0; pixelIndex < totalPixels; pixelIndex += 1) {
    if (visited[pixelIndex]) continue;
    const offset = pixelIndex * 4;
    if (source[offset + 3] <= settings.transparentAlpha) continue;

    const x = pixelIndex % width;
    const y = Math.floor(pixelIndex / width);
    const touchesRemoved =
      (x > 0 && visited[pixelIndex - 1]) ||
      (x + 1 < width && visited[pixelIndex + 1]) ||
      (y > 0 && visited[pixelIndex - width]) ||
      (y + 1 < height && visited[pixelIndex + width]);
    if (!touchesRemoved) continue;

    const distanceSquared = squaredColorDistance(source, offset, edge.color);
    if (distanceSquared > featherLimitSquared) continue;
    const distance = Math.sqrt(distanceSquared);
    const alphaRatio = Math.max(0, Math.min(1, (distance - settings.tolerance) / settings.featherRange));
    const featheredAlpha = Math.round(source[offset + 3] * alphaRatio);
    if (featheredAlpha >= source[offset + 3]) continue;
    output[offset + 3] = featheredAlpha;
    featheredPixels += 1;
  }

  return {
    data: output,
    width,
    height,
    cleanup: {
      applied: true,
      reason: 'edge_connected_background_removed',
      backgroundColor: edge.color,
      edgeCoverage,
      removedOpaquePixels,
      removedRatio,
      retainedOpaqueRatio,
      featheredPixels,
      transparentEdgeRatio,
    },
  };
}

function loadBrowserImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.crossOrigin = 'anonymous';
    image.src = src;
  });
}

export function createMockupArtworkCanvas(image, width, height, options = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  try {
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    const result = removeEdgeConnectedBackground(pixels, options);
    if (result.cleanup.applied) {
      const cleanedPixels = context.createImageData(canvas.width, canvas.height);
      cleanedPixels.data.set(result.data);
      context.putImageData(cleanedPixels, 0, 0);
    }
    return { canvas, cleanup: result.cleanup };
  } catch {
    return {
      canvas,
      cleanup: {
        applied: false,
        reason: 'canvas_pixels_unavailable',
        removedOpaquePixels: 0,
        featheredPixels: 0,
      },
    };
  }
}

export async function createMockupArtworkPreview(src, options = {}) {
  if (!src || typeof src !== 'string') return null;
  const image = await loadBrowserImage(src);
  const maxWidth = Math.max(1, Number(options.maxWidth) || 800);
  const maxHeight = Math.max(1, Number(options.maxHeight) || 800);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) return null;

  const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
  const result = createMockupArtworkCanvas(
    image,
    sourceWidth * scale,
    sourceHeight * scale,
    options,
  );
  if (!result.cleanup.applied) return { url: '', cleanup: result.cleanup };

  return {
    url: result.canvas.toDataURL('image/png'),
    cleanup: result.cleanup,
  };
}
